import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  Card,
  CardMedia,
  Avatar,
  Dialog,
  DialogContent,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function PhotoCarousel({ refreshTrigger }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/photos`);
      // Sort by most recent first (assuming photos have a created_at or similar field)
      const sorted = response.data.sort((a, b) => {
        const dateA = new Date(a.created_at || a.uploaded_at || 0);
        const dateB = new Date(b.created_at || b.uploaded_at || 0);
        return dateB - dateA;
      });
      setPhotos(sorted);
    } catch (error) {
      console.error('Error fetching photos:', error);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
    setCurrentIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Calculate total items
  const totalItems = photos.length;
  
  const handlePrevious = () => {
    if (totalItems === 0) return;
    setCurrentIndex(prev => {
      // Loop to last item if at first
      return prev === 0 ? totalItems - 1 : prev - 1;
    });
  };

  const handleNext = () => {
    if (totalItems === 0) return;
    setCurrentIndex(prev => {
      // Loop to first item if at last
      return prev === totalItems - 1 ? 0 : prev + 1;
    });
  };

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (photos.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No photos yet. Check back soon!
        </Typography>
      </Box>
    );
  }

  // Get previous, current, and next items for peek effect
  const getItemAtIndex = (index) => {
    if (index < 0) {
      return photos[totalItems + index];
    }
    if (index >= totalItems) {
      return photos[index - totalItems];
    }
    return photos[index];
  };

  const prevIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
  const nextIndex = currentIndex === totalItems - 1 ? 0 : currentIndex + 1;
  const prevItem = getItemAtIndex(prevIndex);
  const currentItem = photos[currentIndex];
  const nextItem = getItemAtIndex(nextIndex);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          touchAction: 'pan-y',
          overflow: 'hidden',
          px: { xs: 2, sm: 4 },
        }}
      >
        {/* Left Arrow */}
        {totalItems > 1 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
              flexShrink: 0,
              zIndex: 2,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        {/* Photo Items with Peek Effect */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 1 },
            flex: 1,
            minWidth: 0,
            position: 'relative',
            overflow: 'visible',
            height: '100%',
          }}
        >
          {/* Previous Item (peek) */}
          {totalItems > 1 && (
            <Box
              sx={{
                flex: '0 0 25%',
                maxWidth: '25%',
                opacity: 0.6,
                transform: 'scale(0.85)',
                transition: 'all 0.3s ease',
                pointerEvents: 'none',
                overflow: 'hidden',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Card
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  aspectRatio: '1',
                }}
              >
                <CardMedia
                  component="img"
                  image={`${API_URL}${prevItem.url}`}
                  alt={`Photo by ${prevItem.user_name}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Card>
            </Box>
          )}

          {/* Current Item (full) */}
          <Box
            sx={{
              flex: { xs: '0 0 100%', sm: '0 0 50%' },
              maxWidth: { xs: '100%', sm: '50%' },
              transition: 'all 0.3s ease',
              zIndex: 1,
            }}
          >
            <Card
              onClick={() => {
                setSelectedPhoto(currentItem);
                setModalOpen(true);
              }}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                aspectRatio: '1',
                '&:hover': {
                  transform: 'scale(1.02)',
                  transition: 'transform 0.2s',
                },
              }}
            >
              <CardMedia
                component="img"
                image={`${API_URL}${currentItem.url}`}
                alt={`Photo by ${currentItem.user_name}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Avatar
                  src={currentItem.user_avatar}
                  alt={currentItem.user_name}
                  sx={{
                    width: 24,
                    height: 24,
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {currentItem.user_name?.[0]}
                </Avatar>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {currentItem.user_name}
                </Typography>
              </Box>
            </Card>
          </Box>

          {/* Next Item (peek) */}
          {totalItems > 1 && (
            <Box
              sx={{
                flex: '0 0 25%',
                maxWidth: '25%',
                opacity: 0.6,
                transform: 'scale(0.85)',
                transition: 'all 0.3s ease',
                pointerEvents: 'none',
                overflow: 'hidden',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Card
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  aspectRatio: '1',
                }}
              >
                <CardMedia
                  component="img"
                  image={`${API_URL}${nextItem.url}`}
                  alt={`Photo by ${nextItem.user_name}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Card>
            </Box>
          )}
        </Box>

        {/* Right Arrow */}
        {totalItems > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
              flexShrink: 0,
              zIndex: 2,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>


      {/* Photo Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedPhoto(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => {
              setModalOpen(false);
              setSelectedPhoto(null);
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 3,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedPhoto && (
            <Box>
              <CardMedia
                component="img"
                image={`${API_URL}${selectedPhoto.url}`}
                alt={`Photo by ${selectedPhoto.user_name}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Avatar
                  src={selectedPhoto.user_avatar}
                  alt={selectedPhoto.user_name}
                  sx={{
                    width: 40,
                    height: 40,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {selectedPhoto.user_name?.[0]}
                </Avatar>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {selectedPhoto.user_name}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default PhotoCarousel;

